import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

export interface HelpLink {
  label: string
  description?: string
  href: string // typically help.monofor.com/...
}

export interface HelpLinkGroup {
  title: string
  links: HelpLink[]
}

interface PersonaHelpLinksProps {
  eyebrow?: string
  title: ReactNode
  body?: string
  groups: HelpLinkGroup[]
}

export default function PersonaHelpLinks({
  eyebrow = 'Straight to the docs',
  title,
  body,
  groups,
}: PersonaHelpLinksProps) {
  return (
    <Section tone="default" density="default">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow tone="accent">{eyebrow}</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            {title}
          </Heading>
          {body && (
            <Text size="xl" tone="secondary" className="mt-6">
              {body}
            </Text>
          )}
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-2">
          {groups.map((g, i) => (
            <motion.div
              key={g.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE, delay: (i % 2) * 0.06 }}
              className="rounded-3xl border border-line-subtle bg-surface-subtle p-8 lg:p-10"
            >
              <h3 className="text-display-sm font-medium text-ink-primary">
                {g.title}
              </h3>
              <ul className="mt-6 divide-y divide-line-subtle">
                {g.links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      target={l.href.startsWith('http') ? '_blank' : undefined}
                      rel={l.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="group flex items-start justify-between gap-4 py-4"
                    >
                      <div className="min-w-0">
                        <div className="text-body-md font-medium text-ink-primary group-hover:text-brand-900">
                          {l.label}
                        </div>
                        {l.description && (
                          <div className="mt-0.5 text-body-sm text-ink-tertiary">
                            {l.description}
                          </div>
                        )}
                      </div>
                      <ArrowGlyph className="mt-1 h-4 w-4 flex-shrink-0 text-ink-tertiary transition group-hover:translate-x-0.5 group-hover:text-brand-900" />
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

function ArrowGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
